import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ScanLine } from 'lucide-react';
import CameraCodeScanner from '../../components/scanning/CameraCodeScanner';
import ParcelSortTag from '../../features/delivery/components/ParcelSortTag';

/**
 * WP4 two-party handoff (target-v2). Renders only for workflow_version 2.
 *
 * THE SCAN IS THE RECEIPT (2026-09-25)
 *
 * The store releases an exact parcel list and shows a one-time code. When the
 * assigned Rider scans that live code (or types its 8-digit fallback), the
 * server records custody of the whole released list in the same transaction.
 *
 * There used to be a second step - "I have it / I don't" for every parcel, then
 * "Confirm receipt". It re-confirmed the list the scan had just accepted, ran on
 * the same 5-minute clock as the code, and made every Rider carry an exception
 * the store already handles before releasing ("A Parcel is missing or
 * unavailable"). A Rider who will not take a parcel says so BEFORE scanning,
 * and the store removes it.
 *
 * Checking each parcel's own label is optional evidence, useful with several
 * parcels on the counter. Checks made before the scan are sent with it and
 * recorded as how each parcel was identified; nothing is claimed that did not
 * happen (the server records an unmatched claim as not scanned).
 */

const PARCEL_PATTERN = /^(?:apparify:parcel:v1:)?APY-P1-[0-9A-HJKMNP-TV-Z]{26}$/i;
const canonicalLabel = (value) => String(value || '').trim().replace(/^apparify:parcel:v1:/i, '').toUpperCase();

const SCAN_LABEL = {
  QR: 'Label scanned',
  MANUAL_CODE: 'Label code typed in',
};

// Checks survive a refresh or the screen reloading the trip.
const checksKey = (run) => `apparify:handoff-checks:${run?.id}:${run?.handoff?.manifestVersion || 0}`;
function readChecks(run) {
  try { return JSON.parse(window.sessionStorage.getItem(checksKey(run)) || '{}') || {}; } catch { return {}; }
}
function writeChecks(run, checks) {
  try { window.sessionStorage.setItem(checksKey(run), JSON.stringify(checks)); } catch { /* storage unavailable */ }
}

function HandoffError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{message}
    </p>
  );
}

export default function RiderParcelHandoffCard({ run, busy, onVerifyChallenge, actionError = '' }) {
  // What the trip says should be here. Known from the Run itself, so the Rider
  // can check the Parcels before the store has released anything.
  const expected = useMemo(() => (run?.stops || []).filter(
    (stop) => stop.status === 'QUEUED' && stop.assignmentStatus === 'COMMITTED',
  ), [run?.stops]);
  const released = run?.handoff?.parcels || [];

  const [checks, setChecks] = useState(() => readChecks(run));
  const [checkError, setCheckError] = useState('');
  useEffect(() => { writeChecks(run, checks); }, [run?.id, run?.handoff?.manifestVersion, checks]); // eslint-disable-line react-hooks/exhaustive-deps

  if (Number(run?.workflowVersion) !== 2) return null;

  if (run.handoff?.confirmedAt) {
    return (
      <section className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
        <p className="flex items-center gap-2 font-semibold">
          <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> Parcels received
        </p>
        <p className="mt-1 text-sm">They are now in your care until delivered or returned.</p>
      </section>
    );
  }
  if (!expected.length && !released.length) return null;

  const toCheck = released.length ? released : expected;
  const checkedCount = toCheck.filter((stop) => checks[stop.shipmentId]).length;

  const recordCheck = (value, method) => {
    if (!PARCEL_PATTERN.test(value)) { setCheckError('That is not an Apparify Parcel label.'); return; }
    const code = canonicalLabel(value);
    const match = toCheck.find((stop) => canonicalLabel(stop.parcelLabelCode) === code);
    if (!match) { setCheckError('That parcel is not on this trip. Tell the store before scanning their code.'); return; }
    if (checks[match.shipmentId]) { setCheckError('You already checked that parcel.'); return; }
    setCheckError('');
    setChecks((current) => ({
      ...current,
      [match.shipmentId]: { scanMethod: method === 'QR' ? 'QR' : 'MANUAL_CODE', parcelLabelId: match.parcelLabelId },
    }));
  };

  const labelChecks = Object.entries(checks).map(([shipmentId, check]) => ({
    shipmentId: Number(shipmentId), parcelLabelId: check.parcelLabelId, scanMethod: check.scanMethod,
  }));

  return (
    <section className="mt-4 rounded-2xl border border-stone-200 p-4 dark:border-stone-800" aria-labelledby="handoff-title">
      <div className="flex items-start gap-3">
        <ScanLine className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
        <div>
          <h3 id="handoff-title" className="font-semibold">Parcel handoff</h3>
          <p className="mt-1 text-sm leading-5 text-stone-500 dark:text-stone-400">
            Take the parcels, then scan the store&apos;s QR code. Scanning it confirms you received them.
          </p>
        </div>
      </div>

      <ul className="mt-3 space-y-2" data-testid="handoff-parcels">
        {toCheck.map((stop) => {
          const check = checks[stop.shipmentId];
          return (
            <li key={stop.shipmentId} className="flex items-center justify-between gap-3 rounded-xl bg-stone-50 px-3 py-2 text-xs dark:bg-stone-900">
              <span className="min-w-0">
                <span className="flex items-center gap-2 font-semibold"><ParcelSortTag labelCode={stop.parcelLabelCode} />{stop.orderReference || `Parcel #${stop.shipmentId}`}</span>
                <span className="mt-0.5 block break-all font-mono text-stone-500">{stop.parcelLabelCode || 'No label code'}</span>
              </span>
              {check ? <span className="shrink-0 font-semibold text-emerald-700 dark:text-emerald-300">{SCAN_LABEL[check.scanMethod]}</span> : null}
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-xs leading-5 text-stone-500 dark:text-stone-400">
        Something missing or damaged? Tell the store before you scan, so they can take it off your trip.
      </p>

      {/* Optional evidence: each parcel's own label. Sent with the scan. */}
      <details className="mt-3 rounded-2xl bg-stone-50 p-3 dark:bg-stone-900/60" data-testid="handoff-check" open={checkedCount > 0 || undefined}>
        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
          <span>Optional: check parcel labels</span>
          <span className="text-xs font-normal text-stone-500">{checkedCount} / {toCheck.length} checked</span>
        </summary>
        <p className="mt-1 text-xs leading-5 text-stone-500 dark:text-stone-400">
          Scan the QR on a parcel&apos;s own label to make sure it is the right one. Helpful when several parcels are on the counter.
        </p>
        <div className="mt-3">
          <CameraCodeScanner
            label="Parcel label"
            manualPlaceholder="APY-P1-…"
            manualPattern={PARCEL_PATTERN}
            disabled={busy}
            onDetected={recordCheck}
          />
        </div>
        {checkError && <p role="alert" className="mt-3 rounded-xl bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">{checkError}</p>}
      </details>

      {/* The one required step: the store's live code. */}
      <div className="mt-4" data-testid="handoff-store-code">
        <p className="text-sm font-semibold">Scan the store&apos;s QR code</p>
        {!released.length ? (
          <p className="mt-1 text-xs leading-5 text-stone-500 dark:text-stone-400">
            Ask the store to release your parcels. Their screen then shows a QR code and an 8-digit code for you.
          </p>
        ) : (
          <>
            <p className="mt-1 text-xs leading-5 text-stone-500 dark:text-stone-400">
              Scan the QR code on the store&apos;s screen, or type the 8-digit code they show you. This confirms you received {released.length === 1 ? 'this parcel' : `these ${released.length} parcels`}.
            </p>
            <div className="mt-2">
              <CameraCodeScanner
                label="Rider handoff challenge"
                manualPlaceholder="Eight-digit code"
                manualPattern={/^\d{8}$/}
                manualInputMode="numeric"
                disabled={busy}
                onDetected={(credential, method) => onVerifyChallenge({
                  expectedRunVersion: run.version,
                  manifestVersion: run.handoff.manifestVersion,
                  credential,
                  method,
                  labelChecks,
                })}
              />
            </div>
            <HandoffError message={actionError} />
          </>
        )}
      </div>
    </section>
  );
}
